/**
 * Terrain-elevation sampling helper.
 *
 * The live tracks payload carries the drone's BROADCAST altitude (what the
 * drone tells us via Remote ID), which is whatever altitude reference the
 * vehicle was configured to report — usually MSL from its onboard GPS, but
 * not always trustworthy. Operators also want an independently calculated
 * AGL altitude: broadcast altitude minus the ground elevation directly under
 * the drone. That requires a DEM lookup at the drone's lat/lng.
 *
 * Cesium World Terrain is already attached to the viewer (CesiumMap wires
 * it up via createWorldTerrainAsync) — we just need to sample it. The
 * elevation under a fixed lat/lng doesn't change, so we cache results by
 * coordinates rounded to ~100m precision (3 decimal degrees). Without the
 * cache we'd re-sample every 2-second poll tick as the drone drifts a few
 * meters, which is wasted work.
 *
 * The hook returns a discriminated state so the UI can render "…" while
 * loading and "Unavailable" with a reason when terrain data is missing.
 */
import { useEffect, useState } from 'react';
import { Cartographic, sampleTerrainMostDetailed } from 'cesium';
import { useMapStore } from '../store/mapStore';

export type TerrainElevation =
  | { status: 'loading' }
  | { status: 'ready'; meters: number; source: string }
  | { status: 'unavailable'; reason: string };

// Elevation at a given lat/lng is static, so we cache once per rounded
// coordinate for the lifetime of the page load. 3 decimal degrees ≈ 111m
// at the equator — fine-grained enough that we don't show a hill as flat,
// coarse enough that the drone moving doesn't bust the cache.
const cache = new Map<string, number>();
function cacheKey(lat: number, lng: number): string {
  return `${lat.toFixed(3)}:${lng.toFixed(3)}`;
}

const SOURCE_LABEL = 'Cesium World Terrain';

export function useTerrainElevation(
  lat: number | undefined,
  lng: number | undefined,
): TerrainElevation {
  const viewer = useMapStore((s) => s.viewer);
  const [state, setState] = useState<TerrainElevation>({ status: 'loading' });

  useEffect(() => {
    // No coords → nothing to sample. Reports as unavailable rather than
    // loading so the UI doesn't spin forever on a track with no GPS fix.
    if (lat === undefined || lng === undefined) {
      setState({ status: 'unavailable', reason: 'GPS position unavailable' });
      return;
    }
    if (lat === 0 && lng === 0) {
      setState({ status: 'unavailable', reason: 'GPS position unavailable' });
      return;
    }
    if (!viewer || viewer.isDestroyed()) {
      setState({ status: 'unavailable', reason: 'Map not initialized' });
      return;
    }

    const key = cacheKey(lat, lng);
    const cached = cache.get(key);
    if (cached !== undefined) {
      setState({ status: 'ready', meters: cached, source: SOURCE_LABEL });
      return;
    }

    setState({ status: 'loading' });
    let cancelled = false;

    const provider = viewer.terrainProvider;
    if (!provider) {
      setState({ status: 'unavailable', reason: 'No terrain provider configured' });
      return;
    }

    const point = Cartographic.fromDegrees(lng, lat);
    sampleTerrainMostDetailed(provider, [point])
      .then((points) => {
        if (cancelled) return;
        const height = points[0]?.height;
        if (typeof height === 'number' && Number.isFinite(height)) {
          cache.set(key, height);
          setState({ status: 'ready', meters: height, source: SOURCE_LABEL });
        } else {
          // Terrain provider replied but had no data for this tile — common
          // over open ocean or far outside the loaded tile pyramid.
          setState({
            status: 'unavailable',
            reason: 'Terrain elevation unavailable at this location',
          });
        }
      })
      .catch(() => {
        if (cancelled) return;
        setState({ status: 'unavailable', reason: 'Terrain elevation query failed' });
      });

    return () => {
      cancelled = true;
    };
  }, [viewer, lat, lng]);

  return state;
}
